import styled from "styled-components";
import { useEffect, useState, type FunctionComponent, } from "react";
import { useChainTime, } from "@bracket/platform/hooks";
import { Text as Tx, type SizeVariant, } from "@bracket/ui";
import { DateTime, Interval, } from "luxon";



export declare type TimeWallProps = {
	time: DateTime|"UNKNOWN";
	size?: SizeVariant;
};


/**
 * TimeWall component displays a countdown timer between two dates
 * Shows time remaining in days, hours, and minutes format
 * Changes color based on whether the target time has passed
 * 
 * @param {TimeWallProps} props - Component props
 * @param {string|null} props.time - ISO string of target time to count down to
 * @returns {JSX.Element|null} Formatted time display or null if not ready
 */
export const TimeWall: FunctionComponent<TimeWallProps> = ({
	time,
	size = "lg",
}) => {

	const {
		ready,
		time: nowTime,
	} = useChainTime();

	const [late, setLate] = useState(false);
	const [days, setDays] = useState("");
	const [hours, setHours] = useState("");
	const [minutes, setMinutes] = useState("");
	const [ticked, setTicked] = useState(false);

	let tick: NodeJS.Timeout|null = null;

	const onTick = () => {

		if(!time)
			return;
		
		const now = DateTime.fromJSDate(nowTime);
		const when = typeof time === "string" ? DateTime.fromISO(time) : time;
		const late = now > when;
		const inte = late ? Interval.fromDateTimes(when, now) : Interval.fromDateTimes(now, when);
		const {
			days,
			hours,
			minutes,
		} = inte.toDuration(['days', 'minutes', 'hours']);

		const daysStr = days.toString();

		let hoursString = hours.toString();
		hoursString = hoursString.length === 1 ? `0${hoursString}` : hoursString;

		let minString = minutes.toFixed(0);
		minString = minString.length === 1 ? `0${minString}` : minString;
		if(daysStr === "NaN") {
			return;
		}
		setHours(hoursString);
		setDays(daysStr);
		setMinutes(minString);
		setLate(late);
		setTicked(true);
	};

	useEffect(() => {
		if(ready && time !== "UNKNOWN") {
			onTick();
			tick = setInterval(() => {
				onTick();
			}, 1000);
			return () => {
				clearInterval(tick as NodeJS.Timeout);
			};
		}
	}, [
		ready,
		time,
		nowTime,
	]);

	if(time === "UNKNOWN")
		return <Tx size={size} color="primary">Unknown</Tx>;


	if(!ticked || !ready) {
		return null;
	}

	return (
		<Brace>
			<Tx size={size} color={late ? "error" : "primary"}>
				{late ? "-" : ""} {days}d {hours}h {minutes}m
			</Tx>
		</Brace>
	);
}


const Brace = styled.div`

`
